import React, { useContext, useState } from "react";
import Card from "../../../Card";
import MainContext from "../../../../Context/MainContext/MainContext";
import User from "../../../../Utility/Crud/User";
import Role from "../../../../Utility/Crud/Role";
import Table from "../../../Table";
import api from "../../../../Seating/Security/api";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

// MUI Imports
import MUIBox from "@mui/material/Box";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIButton from "@mui/material/Button";
import MUITypography from "@mui/material/Typography";


export default function CardSalesPcrLinksList(props) {
    // Context and local UI state.
    const mainContext = useContext(MainContext);
    const mode = props.mode || "PCR";
    const selectedLocationIds = props.selectedLocationIds || [];
    const [selectedIdsByRowId, setSelectedIdsByRowId] = useState({});
    const [isSavingByRowId, setIsSavingByRowId] = useState({});
    const [tableRenderKey, setTableRenderKey] = useState(0);
    const salesRepRoleIds = [52, 55, 56];
    const pcrRoleIds = [77, 4, 42];

    /**
     * Map of userId -> Set(locationId) for quick shared-location checks.
     */
    const locationIdsByUserId = Object.values(mainContext.userprofileLocationsIndexed || {}).reduce((accumulator, userprofileLocation) => {
        if (!accumulator[userprofileLocation.userprofileId]) {
            accumulator[userprofileLocation.userprofileId] = new Set();
        }

        accumulator[userprofileLocation.userprofileId].add(userprofileLocation.locationsId);
        return accumulator;
    }, {});

    const salesPcrLinks = Object.values(mainContext.salesPcrLinksIndexed).filter((salesPcrLink) => {
        const pcrUser = mainContext.usersIndexed[salesPcrLink.patientCareRepId];
        const salesUser = mainContext.usersIndexed[salesPcrLink.salesRepId];
        return (
            pcrUser?.active === true &&
            salesUser?.active === true
        );
    });

    const pcrUsers = User.getByRole(
        mainContext.usersIndexed,
        mainContext.userprofileLocationsIndexed,
        pcrRoleIds,
        selectedLocationIds
    );
    const salesRepUsers = User.getByRole(
        mainContext.usersIndexed,
        mainContext.userprofileLocationsIndexed,
        salesRepRoleIds,
        selectedLocationIds
    );

    /**
     * Maps userId -> sorted role display names from userprofile_role rows.
     */
    const roleNamesByUserId = Object.values(mainContext.userprofileRolesIndexed || {}).reduce((accumulator, userprofileRole) => {
        if (userprofileRole.deleted === true) {
            return accumulator;
        }

        const userId = userprofileRole.userProfileId ?? userprofileRole.userprofileId;
        if (!userId) {
            return accumulator;
        }

        const role = mainContext.rolesIndexed[userprofileRole.roleId];
        if (!role) {
            return accumulator;
        }

        if (!accumulator[userId]) {
            accumulator[userId] = new Set();
        }

        accumulator[userId].add(Role.getDisplayName(role));
        return accumulator;
    }, {});

    const getUserRoleNames = (userId) => {
        const roleNamesSet = roleNamesByUserId[userId];
        if (!roleNamesSet) {
            return [];
        }

        return [...roleNamesSet].sort((a, b) => a.localeCompare(b));
    };

    const getUserCell = (user) => {
        const roleNames = getUserRoleNames(user.id);

        return {
            content: (
                <MUIBox>
                    <span>{User.getDisplayName(user)}</span>
                    {roleNames.length > 0 && (
                        <MUIBox sx={{ marginTop: 0.25 }}>
                            {roleNames.map((roleName) => (
                                <MUITypography key={roleName} variant="caption" color="text.secondary" component="div">
                                    {roleName}
                                </MUITypography>
                            ))}
                        </MUIBox>
                    )}
                </MUIBox>
            ),
            searchValue: `${User.getDisplayName(user)} ${roleNames.join(" ")}`.trim(),
        };
    };
    /**
     * Sorts user IDs by display name order and returns the sorted ID list.
     *
     * @param {number[]} userIds
     * @returns {number[]}
     */
    const getSortedUserIds = (userIds) => {
        return User.sort(
            userIds
                .map((userId) => mainContext.usersIndexed[userId])
                .filter(Boolean)
        ).map((user) => user.id);
    };

    // Maps a PCR user ID to its Sales PCR Link rows.
    const salesPcrLinksByPatientCareRepId = {}
    salesPcrLinks.forEach(salesPcrLink => {
        if (salesPcrLinksByPatientCareRepId[salesPcrLink.patientCareRepId] === undefined) {
            salesPcrLinksByPatientCareRepId[salesPcrLink.patientCareRepId] = [];
        }
        salesPcrLinksByPatientCareRepId[salesPcrLink.patientCareRepId].push({
            id: salesPcrLink.id,
            salesRepId: salesPcrLink.salesRepId,
        });
    });

    // Table rows for PCR mode.
    const pcrNamesList = pcrUsers.map((pcrUser) => {
        const salesPcrLinksForPatientCareRep = salesPcrLinksByPatientCareRepId[pcrUser.id] || [];
        const sortedSalesRepIds = getSortedUserIds(salesPcrLinksForPatientCareRep.map((salesPcrLink) => salesPcrLink.salesRepId));
        const salesRepNames = sortedSalesRepIds.map((salesRepId) => {
            return User.getDisplayName(mainContext.usersIndexed[salesRepId]);
        }).join(", ");
        return {
            id: pcrUser.id,
            salesPcrLinksForPatientCareRep,
            salesRepIds: sortedSalesRepIds,
            user: getUserCell(pcrUser),
            salesRepNames: salesRepNames || "No Linked Sales Reps",
        };
    });

    // Maps a sales rep user ID to its Sales PCR Link rows.
    const salesPcrLinksBySalesRepId = {}
    salesPcrLinks.forEach(salesPcrLink => {
        if (salesPcrLinksBySalesRepId[salesPcrLink.salesRepId] === undefined) {
            salesPcrLinksBySalesRepId[salesPcrLink.salesRepId] = [];
        }
        salesPcrLinksBySalesRepId[salesPcrLink.salesRepId].push({
            id: salesPcrLink.id,
            patientCareRepId: salesPcrLink.patientCareRepId,
        });
    });

    // Table rows for Sales mode.
    const salesNamesList = salesRepUsers.map((salesRepUser) => {
        const salesPcrLinksForSalesRep = salesPcrLinksBySalesRepId[salesRepUser.id] || [];
        const sortedPcrUserIds = getSortedUserIds(salesPcrLinksForSalesRep.map((salesPcrLink) => salesPcrLink.patientCareRepId));
        const pcrUserNames = sortedPcrUserIds.map((pcrUserId) => {
            return User.getDisplayName(mainContext.usersIndexed[pcrUserId]);
        }).join(", ");
        return {
            id: salesRepUser.id,
            salesPcrLinksForSalesRep,
            pcrUserIds: sortedPcrUserIds,
            user: getUserCell(salesRepUser),
            pcrUserNames: pcrUserNames || "No Linked PCRs/ATPs",
        };
    });

    /**
     * Saves one expanded row by diffing selected related users against
     * existing Sales PCR Link rows and then applying create/delete changes.
     *
     * @param {object} row
     * @param {number[]} optionsIds
     * @returns {void}
     */
    const handleSaveRow = (row, optionsIds) => {
        const selectedIds = [...new Set((selectedIdsByRowId[row.id] || optionsIds).map((id) => +id))].filter((id) => Number.isFinite(id));
        const selectedIdsSet = new Set(selectedIds.map((id) => String(id)));
        const originalSalesPcrLinks = mode === "PCR" ? row.salesPcrLinksForPatientCareRep : row.salesPcrLinksForSalesRep;
        const originalIds = mode === "PCR"
            ? originalSalesPcrLinks.map((salesPcrLink) => salesPcrLink.salesRepId)
            : originalSalesPcrLinks.map((salesPcrLink) => salesPcrLink.patientCareRepId);
        const originalIdsSet = new Set(originalIds.map((id) => String(id)));

        const idsToAdd = selectedIds.filter((id) => !originalIdsSet.has(String(id)));
        const salesPcrLinksToRemove = originalSalesPcrLinks.filter((salesPcrLink) => {
            const targetId = mode === "PCR" ? salesPcrLink.salesRepId : salesPcrLink.patientCareRepId;
            return !selectedIdsSet.has(String(targetId));
        });

        setIsSavingByRowId({
            ...isSavingByRowId,
            [row.id]: true,
        });

        const addPromises = idsToAdd.map((idToAdd) => {
            return api.send("POST", "/users/salesPCRLinks", {
                name: "nothing",
                patientCareRepId: mode === "PCR" ? row.id : idToAdd,
                salesRepId: mode === "PCR" ? idToAdd : row.id,
            }).then((createdSalesPcrLink) => {
                mainContext.updateIndexed("salesPcrLinksIndexed", createdSalesPcrLink);
                return createdSalesPcrLink;
            });
        });

        const removePromises = salesPcrLinksToRemove.map((salesPcrLinkToRemove) => {
            return api.send("DELETE", `/users/salesPCRLinks/${salesPcrLinkToRemove.id}`).then(() => {
                mainContext.deleteIndexed("salesPcrLinksIndexed", salesPcrLinkToRemove.id);
                return salesPcrLinkToRemove;
            });
        });

        Promise.all([...addPromises, ...removePromises])
            .then(() => {
                NotistackEnqueueSnackbar("PCR links updated successfully!", { variant: "success" });
                setSelectedIdsByRowId({
                    ...selectedIdsByRowId,
                    [row.id]: selectedIds,
                });
                setTableRenderKey((prev) => prev + 1);
            })
            .catch((err) => {
                console.log(err);
                NotistackEnqueueSnackbar("Oh no! There was an error updating PCR links.", { variant: "error" });
            })
            .finally(() => {
                setIsSavingByRowId((prev) => ({
                    ...prev,
                    [row.id]: false,
                }));
            });
    };

    /**
     * Renders the expandable row editor for assigning related users.
     *
     * @param {object} row
     * @returns {JSX.Element}
     */
    const accordianRenderFunction = (row) => {
        const optionsIds = mode === "PCR" ? row.salesRepIds : row.pcrUserIds;
        const allOptions = mode === "PCR" ? salesRepUsers : pcrUsers;
        const rowUserLocationIds = locationIdsByUserId[row.id] || new Set();
        const rowFilteredLocationIds = new Set(
            [...rowUserLocationIds].filter((locationId) => selectedLocationIds.includes(locationId))
        );
        const allOptionsWithCommonLocation = allOptions.filter((userOption) => {
            const optionLocationIds = locationIdsByUserId[userOption.id] || new Set();

            for (const locationId of optionLocationIds) {
                if (rowFilteredLocationIds.has(locationId)) {
                    return true;
                }
            }

            return false;
        });
        const selectedIds = [...new Set((selectedIdsByRowId[row.id] || optionsIds).map((id) => +id))].filter((id) => Number.isFinite(id));
        const selectedIdsSet = new Set(selectedIds.map((id) => String(id)));
        const originalSalesPcrLinks = mode === "PCR" ? row.salesPcrLinksForPatientCareRep : row.salesPcrLinksForSalesRep;
        const originalIds = mode === "PCR"
            ? originalSalesPcrLinks.map((salesPcrLink) => salesPcrLink.salesRepId)
            : originalSalesPcrLinks.map((salesPcrLink) => salesPcrLink.patientCareRepId);
        const originalIdsSet = new Set(originalIds.map((id) => String(id)));
        const hasChangesToSave = selectedIds.length !== originalIds.length || selectedIds.some((id) => !originalIdsSet.has(String(id))) || originalIds.some((id) => !selectedIdsSet.has(String(id)));
        const selectedOptions = (selectedIdsByRowId[row.id] || optionsIds)
            .map((optionId) => mainContext.usersIndexed[optionId])
            .filter(Boolean);

        return (
            <MUIBox sx={{ display: "flex", alignItems: "flex-end", gap: 3 }}>
                <MUIAutocomplete
                    fullWidth
                    options={allOptionsWithCommonLocation}
                    multiple
                    value={selectedOptions}
                    onChange={(_, newValue) => {
                        setSelectedIdsByRowId({
                            ...selectedIdsByRowId,
                            [row.id]: newValue.map((option) => option.id),
                        });
                    }}
                    getOptionLabel={(option) => User.getDisplayName(option)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                        <MUITextField
                            {...params}
                            label={mode === "PCR" ? "Sales Reps" : "PCRs/ATPs"}
                            variant="standard"
                            InputLabelProps={{ shrink: true }}
                        />
                    )} />
                <MUIButton
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={isSavingByRowId[row.id] === true || hasChangesToSave === false}
                    onClick={() => handleSaveRow(row, optionsIds)}
                >
                    Save
                </MUIButton>
            </MUIBox>)
    }

    /**
     * Renders the table for the current mode.
     *
     * Note: changing tableRenderKey remounts the table, which closes any open
     * accordion rows after a successful save.
     *
     * @returns {JSX.Element}
     */
    const renderContent = () => (
        <>
            <Table
                key={`${mode}-${tableRenderKey}`}
                columns={mode === "PCR" ? [
                    { key: "user", name: "User", type: "custom", width: "260px" },
                    { key: "salesRepNames", name: "Linked Sales Reps", type: "string" },
                ] : [
                    { key: "user", name: "User", type: "custom", width: "260px" },
                    { key: "pcrUserNames", name: "Linked PCRs/ATPs", type: "string" },
                ]}
                rows={mode === "PCR" ? pcrNamesList : salesNamesList}
                search={true}
                accordianRenderFunction={(row) => accordianRenderFunction(row)}
            />

        </>
    );

    return (
        <Card renderContent={renderContent} />
    );
}
